import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Truck,
  Clock,
  MapPinned,
  FileCheck,
  ClipboardList,
  CalendarCheck,
  PackageCheck,
  UserCheck,
  PackageOpen,
  Network,
  CarFront,
  Container,
  Phone,
} from "lucide-react";
import Seo, { organizationJsonLd } from "@/components/Seo";
import Reveal from "@/components/Reveal";
import QuoteCalculator from "@/components/QuoteCalculator";
import { COMPANY, SERVICES, VEHICLES, IMAGES } from "@/config/site";

const ICONS = { Truck, UserCheck, PackageOpen, Network, CarFront, Container };

const REASSURANCE = [
  { icon: ShieldCheck, text: "Transporteur licencié" },
  { icon: Truck, text: "Véhicules de 3,5 T à 40 T" },
  { icon: UserCheck, text: "Chauffeurs expérimentés" },
  { icon: FileCheck, text: "Marchandises assurées (AXA)" },
  { icon: MapPinned, text: "France et Europe" },
  { icon: Clock, text: "Devis sous 24 h ouvrées" },
];

const STEPS = [
  { icon: ClipboardList, title: "Vous décrivez votre besoin", text: "Adresses, marchandise, poids, dates : 2 minutes via le calculateur ou le formulaire." },
  { icon: FileCheck, title: "Devis sous 24 h ouvrées", text: "Un devis précis, gratuit et sans engagement, valable 15 jours." },
  { icon: CalendarCheck, title: "Planification", text: "Nous convenons ensemble des dates d'enlèvement et de livraison." },
  { icon: PackageCheck, title: "Livraison et suivi", text: "Vos marchandises arrivent à destination, avec suivi sur demande." },
];

const Home = () => {
  return (
    <>
      <Seo
        title="ASTROLOG — Transport routier de marchandises et déménagement en France et en Europe"
        description="Transport routier de marchandises, location de camions avec conducteur et déménagement. Estimation en ligne immédiate, devis sous 24 h ouvrées. France et Europe."
        path="/"
        jsonLd={organizationJsonLd}
      />

      {/* ============ HERO ============ */}
      <section className="brand-dark noise relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Camion ASTROLOG sur autoroute"
            className="h-full w-full object-cover opacity-40"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-[#0A0E1A]/60" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 pb-40 pt-20 sm:px-6 sm:pt-28 lg:px-8">
          <Reveal>
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-secondary">
              <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
              Transport · Logistique · Déménagement
            </p>
            <h1 className="max-w-4xl font-heading text-4xl font-bold leading-[1.05] tracking-tighter text-[#FAFAF7] sm:text-5xl lg:text-6xl">
              Transport routier de marchandises et déménagement en{" "}
              <span className="text-accent">France</span> et en{" "}
              <span className="text-secondary">Europe</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Du fourgon à la semi-remorque 40 T, ASTROLOG achemine vos
              marchandises et organise vos déménagements avec des chauffeurs
              expérimentés. Devis gratuit sous 24 h ouvrées.
            </p>
            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <a
                href="#calculateur"
                data-testid="hero-estimate-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-accent px-8 py-4 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#EA580C] hover:shadow-xl"
              >
                Estimer mon transport
                <ArrowRight className="h-5 w-5" />
              </a>
              <Link
                to="/contact"
                data-testid="hero-devis-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-heading text-base font-semibold text-[#FAFAF7] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-white/10"
              >
                Demander un devis
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ CALCULATEUR COMPACT ============ */}
      <section id="calculateur" className="relative z-10 mx-auto -mt-24 max-w-5xl scroll-mt-28 px-4 sm:px-6 lg:px-8">
        <Reveal>
          <QuoteCalculator variant="compact" />
        </Reveal>
      </section>

      {/* ============ BANDEAU RÉASSURANCE ============ */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8" data-testid="reassurance-section">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {REASSURANCE.map((r, i) => (
            <Reveal key={r.text} delay={i * 60}>
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg">
                <r.icon className="h-7 w-7 text-primary" strokeWidth={1.75} />
                <p className="text-sm font-semibold text-foreground">{r.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ PRÉSENTATION ============ */}
      <section className="brand-dark noise relative py-16 sm:py-20 lg:py-24" data-testid="about-preview-section">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <Reveal>
            <p className="text-sm font-semibold uppercase tracking-widest text-secondary">Qui sommes-nous</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-[#FAFAF7] sm:text-4xl">
              Un transporteur de confiance, basé en Aube
            </h2>
            <p className="mt-5 leading-relaxed text-slate-300">
              ASTROLOG est une SAS au capital de {COMPANY.capital}, immatriculée
              au RCS de Troyes le 18 juillet 2022 et en activité depuis le 1er
              juillet 2022. Nous assurons le transport public routier de
              marchandises, la location de véhicules industriels avec
              conducteurs, le déménagement et la commission de transport.
            </p>
            <p className="mt-4 leading-relaxed text-slate-400">
              Titulaires de la licence communautaire de transport et assurés
              auprès d'AXA France IARD, nous intervenons en France métropolitaine
              et en Europe avec des véhicules de 3,5 T à 40 T.
            </p>
            <Link
              to="/a-propos"
              data-testid="about-preview-link"
              className="mt-7 inline-flex items-center gap-2 font-semibold text-accent transition-colors hover:text-[#EA580C]"
            >
              Découvrir l'entreprise <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={150}>
            <div className="relative">
              <img
                src={IMAGES.warehouse}
                alt="Entrepôt logistique ASTROLOG"
                loading="lazy"
                width={1600}
                height={1067}
                className="aspect-[4/3] w-full rounded-3xl object-cover"
              />
              <div className="absolute -bottom-6 -left-6 rounded-2xl bg-accent p-5 shadow-xl">
                <p className="font-heading text-3xl font-bold text-white">24 h</p>
                <p className="text-sm font-medium text-white/90">pour recevoir votre devis</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SERVICES ============ */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24" data-testid="services-preview-section">
        <Reveal className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary">Nos prestations</p>
          <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
            Six métiers du transport, un seul interlocuteur
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            return (
              <Reveal key={s.id} delay={(i % 3) * 80}>
                <article className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-lg">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 transition-colors duration-300 group-hover:bg-accent/15">
                    <Icon className="h-6 w-6 text-primary transition-colors duration-300 group-hover:text-accent" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-semibold">{s.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{s.description}</p>
                  <Link
                    to={`/contact?service=${s.id}`}
                    data-testid={`service-card-cta-${s.id}`}
                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary transition-colors hover:text-accent"
                  >
                    Demander un devis pour ce service <ArrowRight className="h-4 w-4" />
                  </Link>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ============ MÉTHODE ============ */}
      <section className="brand-dark noise relative py-16 sm:py-20 lg:py-24" data-testid="method-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-secondary">Notre méthode</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight text-[#FAFAF7] sm:text-4xl">
              Votre transport en 4 étapes
            </h2>
          </Reveal>
          <div className="relative mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            <div className="route-line absolute left-0 right-0 top-7 hidden lg:block" aria-hidden="true" />
            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 100}>
                <div className="relative">
                  <span className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#111418] ring-1 ring-white/15">
                    <step.icon className="h-7 w-7 text-secondary" strokeWidth={1.75} />
                  </span>
                  <p className="mt-5 font-heading text-sm font-bold uppercase tracking-widest text-accent">
                    Étape {i + 1}
                  </p>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-[#FAFAF7]">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-400">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FLOTTE APERÇU ============ */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24" data-testid="fleet-preview-section">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary">Notre flotte</p>
            <h2 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
              Du fourgon à la semi-remorque
            </h2>
          </div>
          <Link
            to="/flotte"
            data-testid="fleet-preview-link"
            className="inline-flex items-center gap-2 font-semibold text-primary transition-colors hover:text-accent"
          >
            Voir toute la flotte <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VEHICLES.slice(0, 4).map((v, i) => (
            <Reveal key={v.id} delay={i * 80}>
              <Link to="/flotte" className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-lg">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={v.image}
                    alt={v.name}
                    loading="lazy"
                    width={1200}
                    height={675}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-heading text-base font-semibold">{v.name}</h3>
                  <p className="mt-1 text-sm text-slate-500">
                    {v.payload.toLocaleString("fr-FR")} kg · ~{v.volume} m³
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ==========================================================
          Bloc « Avis clients » désactivé volontairement :
          aucun témoignage ne sera publié sans avis clients réels.
         ========================================================== */}

      {/* ============ CTA FINAL ============ */}
      <section className="brand-dark noise relative py-16 sm:py-20 lg:py-24" data-testid="final-cta-section">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <h2 className="font-heading text-3xl font-bold tracking-tight text-[#FAFAF7] sm:text-4xl lg:text-5xl">
              Un transport à organiser ?<br />
              <span className="text-accent">Estimez votre budget en 30 secondes.</span>
            </h2>
            <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/calculateur"
                data-testid="final-cta-calculator"
                className="inline-flex items-center gap-2 rounded-full bg-accent px-8 py-4 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-1 hover:bg-[#EA580C] hover:shadow-xl"
              >
                Lancer le calculateur <ArrowRight className="h-5 w-5" />
              </Link>
              <a
                href={COMPANY.phoneHref}
                data-testid="final-cta-phone"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-8 py-4 font-heading text-base font-semibold text-[#FAFAF7] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-white/10"
              >
                <Phone className="h-5 w-5" /> {COMPANY.phone}
              </a>
            </div>
            <p className="mt-5 text-sm text-slate-400">
              Ou écrivez-nous :{" "}
              <a href={COMPANY.emailHref} className="font-medium text-secondary hover:underline">
                {COMPANY.email}
              </a>
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Home;
